import {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Link,
    useParams,
} from "react-router-dom";

import {
    FiArrowLeft,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiEdit2,
    FiExternalLink,
    FiFileText,
    FiMapPin,
    FiNavigation,
    FiPhone,
    FiUser,
    FiUsers,
    FiXCircle,
} from "react-icons/fi";

import {
    getVisit,
    getVisitRoute,
} from "services/api/visitApi";

import {
    showError,
} from "utils/toast";


// ============================================================
// HELPERS
// ============================================================

const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    try {

        return new Date(
            value
        ).toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );

    } catch (
    error
    ) {

        return "-";

    }

};


const formatDateTime = (
    value
) => {

    if (!value) {
        return "-";
    }

    try {

        return new Date(
            value
        ).toLocaleString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }
        );

    } catch (
    error
    ) {

        return "-";

    }

};


const formatNumber = (
    value
) => {

    const number =
        Number(
            value || 0
        );

    return Number.isFinite(
        number
    )
        ? number.toFixed(2)
        : "0.00";

};


const formatCurrency = (
    value
) => {

    const number =
        Number(
            value || 0
        );

    if (
        !Number.isFinite(
            number
        )
    ) {

        return "₹0.00";

    }

    return `₹${number.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;

};


const getArray = (
    value
) => {

    return Array.isArray(
        value
    )
        ? value
        : [];

};


const getStatusClass = (
    status
) => {

    switch (
    status
    ) {

        case "planned":

            return "bg-yellow-100 text-yellow-700";

        case "started":

            return "bg-indigo-100 text-indigo-700";

        case "submitted":

            return "bg-blue-100 text-blue-700";

        case "approved":

            return "bg-green-100 text-green-700";

        case "rejected":

            return "bg-red-100 text-red-700";

        case "correction_requested":

            return "bg-orange-100 text-orange-700";

        case "cancelled":

            return "bg-gray-200 text-gray-600";

        default:

            return "bg-gray-100 text-gray-700";

    }

};


const getStatusLabel = (
    status
) => {

    if (!status) {
        return "-";
    }

    return status
        .replace(
            /_/g,
            " "
        )
        .replace(
            /\b\w/g,
            (character) =>
                character.toUpperCase()
        );

};


// ============================================================
// INFO FIELD
// ============================================================

const InfoField = ({
    label,
    value,
    icon: Icon,
    className = "",
}) => {

    return (

        <div
            className={`
                rounded-lg
                border
                border-gray-100
                bg-gray-50
                p-4
                ${className}
            `}
        >

            <div
                className="
                    mb-1
                    flex
                    items-center
                    gap-2
                    text-xs
                    font-medium
                    text-gray-500
                "
            >

                {
                    Icon && (
                        <Icon
                            className="
                                text-gray-400
                            "
                        />
                    )
                }

                {label}

            </div>


            <div
                className="
                    break-words
                    text-sm
                    font-semibold
                    text-gray-800
                "
            >

                {
                    value === null ||
                        value === undefined ||
                        value === ""
                        ? "-"
                        : value
                }

            </div>

        </div>

    );

};


// ============================================================
// SECTION
// ============================================================

const Section = ({
    title,
    subtitle,
    children,
    right,
}) => {

    return (

        <section
            className="
                rounded-xl
                border
                border-gray-100
                bg-white
                p-5
                shadow-sm
            "
        >

            <div
                className="
                    mb-5
                    flex
                    flex-col
                    gap-2
                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <h3
                        className="
                            text-lg
                            font-bold
                            text-gray-800
                        "
                    >

                        {title}

                    </h3>


                    {
                        subtitle && (

                            <p
                                className="
                                    mt-1
                                    text-xs
                                    text-gray-500
                                "
                            >

                                {subtitle}

                            </p>

                        )
                    }

                </div>


                {
                    right && (
                        <div>
                            {right}
                        </div>
                    )
                }

            </div>


            {children}

        </section>

    );

};


// ============================================================
// EMPTY STATE
// ============================================================

const EmptyState = ({
    text = "No records available.",
}) => {

    return (

        <div
            className="
                rounded-lg
                border
                border-dashed
                border-gray-200
                bg-gray-50
                p-5
                text-center
                text-sm
                text-gray-500
            "
        >

            {text}

        </div>

    );

};


// ============================================================
// ROUTE MAP / VISUALIZATION
// ============================================================
//
// No external map package required.
//
// Uses actual latitude/longitude points returned by the API
// and draws the actual recorded sequence.
//
// ============================================================

const RouteMap = ({
    points,
}) => {

    const validPoints =
        getArray(
            points
        ).filter(
            (point) => {

                const latitude =
                    Number(
                        point.latitude
                    );

                const longitude =
                    Number(
                        point.longitude
                    );

                return (
                    Number.isFinite(
                        latitude
                    ) &&
                    Number.isFinite(
                        longitude
                    )
                );

            }
        );


    if (
        validPoints.length === 0
    ) {

        return (

            <EmptyState
                text="
                    No GPS route points have been recorded for this visit.
                "
            />

        );

    }


    const width =
        900;

    const height =
        380;

    const padding =
        55;


    const latitudes =
        validPoints.map(
            (point) =>
                Number(
                    point.latitude
                )
        );


    const longitudes =
        validPoints.map(
            (point) =>
                Number(
                    point.longitude
                )
        );


    let minLat =
        Math.min(
            ...latitudes
        );

    let maxLat =
        Math.max(
            ...latitudes
        );

    let minLng =
        Math.min(
            ...longitudes
        );

    let maxLng =
        Math.max(
            ...longitudes
        );


    /*
    |--------------------------------------------------------------------------
    | Prevent zero-width / zero-height route
    |--------------------------------------------------------------------------
    */

    if (
        minLat === maxLat
    ) {

        minLat -= 0.001;

        maxLat += 0.001;

    }


    if (
        minLng === maxLng
    ) {

        minLng -= 0.001;

        maxLng += 0.001;

    }


    const projectPoint = (
        point
    ) => {

        const latitude =
            Number(
                point.latitude
            );

        const longitude =
            Number(
                point.longitude
            );


        const x =
            padding +
            (
                (
                    longitude -
                    minLng
                ) /
                (
                    maxLng -
                    minLng
                )
            ) *
            (
                width -
                padding * 2
            );


        const y =
            height -
            padding -
            (
                (
                    latitude -
                    minLat
                ) /
                (
                    maxLat -
                    minLat
                )
            ) *
            (
                height -
                padding * 2
            );


        return {
            x,
            y,
        };

    };


    const projected =
        validPoints.map(
            (
                point
            ) => ({
                ...point,
                ...projectPoint(
                    point
                ),
            })
        );


    const polyline =
        projected
            .map(
                (
                    point
                ) =>
                    `${point.x},${point.y}`
            )
            .join(
                " "
            );


    return (

        <div
            className="
                overflow-hidden
                rounded-xl
                border
                border-gray-200
                bg-gray-50
            "
        >

            <div
                className="
                    flex
                    items-center
                    justify-between
                    border-b
                    border-gray-200
                    bg-white
                    px-4
                    py-3
                "
            >

                <div
                    className="
                        flex
                        items-center
                        gap-2
                        text-sm
                        font-semibold
                        text-gray-700
                    "
                >

                    <FiNavigation />

                    Actual Recorded Route

                </div>


                <span
                    className="
                        text-xs
                        text-gray-500
                    "
                >

                    {
                        validPoints.length
                    }

                    {" GPS points"}

                </span>

            </div>


            <div
                className="
                    overflow-x-auto
                    p-3
                "
            >

                <svg
                    viewBox={`
                        0 0
                        ${width}
                        ${height}
                    `}
                    className="
                        min-w-[700px]
                        w-full
                    "
                >

                    {/* Background grid */}

                    <line
                        x1="55"
                        y1="55"
                        x2="845"
                        y2="55"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />

                    <line
                        x1="55"
                        y1="190"
                        x2="845"
                        y2="190"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />

                    <line
                        x1="55"
                        y1="325"
                        x2="845"
                        y2="325"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />

                    <line
                        x1="55"
                        y1="55"
                        x2="55"
                        y2="325"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />

                    <line
                        x1="450"
                        y1="55"
                        x2="450"
                        y2="325"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />

                    <line
                        x1="845"
                        y1="55"
                        x2="845"
                        y2="325"
                        stroke="#e5e7eb"
                        strokeWidth="1"
                    />


                    {/* Actual route */}

                    {
                        projected.length > 1 && (

                            <polyline
                                points={
                                    polyline
                                }
                                fill="none"
                                stroke="#4f46e5"
                                strokeWidth="5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />

                        )
                    }


                    {/* Route points */}

                    {
                        projected.map(
                            (
                                point,
                                index
                            ) => {

                                const isStart =
                                    index === 0;

                                const isEnd =
                                    index ===
                                    projected.length - 1;


                                return (

                                    <g
                                        key={
                                            point.id ||
                                            `${point.latitude}-${point.longitude}-${index}`
                                        }
                                    >

                                        <circle
                                            cx={
                                                point.x
                                            }
                                            cy={
                                                point.y
                                            }
                                            r={
                                                isStart ||
                                                    isEnd
                                                    ? 10
                                                    : 7
                                            }
                                            fill={
                                                isStart
                                                    ? "#16a34a"
                                                    : isEnd
                                                        ? "#dc2626"
                                                        : "#4f46e5"
                                            }
                                            stroke="white"
                                            strokeWidth="3"
                                        />


                                        <text
                                            x={
                                                point.x + 13
                                            }
                                            y={
                                                point.y - 12
                                            }
                                            fontSize="13"
                                            fontWeight="600"
                                            fill="#374151"
                                        >

                                            {
                                                isStart
                                                    ? "START"
                                                    : isEnd
                                                        ? "END"
                                                        : `P${index + 1}`
                                            }

                                        </text>

                                    </g>

                                );

                            }
                        )
                    }

                </svg>

            </div>


            <div
                className="
                    flex
                    flex-wrap
                    gap-4
                    border-t
                    border-gray-200
                    bg-white
                    px-4
                    py-3
                    text-xs
                    text-gray-600
                "
            >

                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    <span
                        className="
                            h-3
                            w-3
                            rounded-full
                            bg-green-600
                        "
                    />

                    Start

                </div>


                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    <span
                        className="
                            h-3
                            w-3
                            rounded-full
                            bg-indigo-600
                        "
                    />

                    Tracking Point

                </div>


                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    <span
                        className="
                            h-3
                            w-3
                            rounded-full
                            bg-red-600
                        "
                    />

                    End

                </div>

            </div>

        </div>

    );

};


// ============================================================
// ROUTE TABLE
// ============================================================

const RouteTable = ({
    points,
}) => {

    const validPoints =
        getArray(
            points
        );


    if (
        validPoints.length === 0
    ) {

        return (

            <EmptyState
                text="
                    No route points recorded.
                "
            />

        );

    }


    return (

        <div
            className="
                overflow-x-auto
            "
        >

            <table
                className="
                    min-w-full
                    text-sm
                "
            >

                <thead>

                    <tr
                        className="
                            border-b
                            border-gray-200
                            text-left
                            text-xs
                            uppercase
                            text-gray-500
                        "
                    >

                        <th className="px-3 py-3">
                            #
                        </th>

                        <th className="px-3 py-3">
                            Point
                        </th>

                        <th className="px-3 py-3">
                            Latitude
                        </th>

                        <th className="px-3 py-3">
                            Longitude
                        </th>

                        <th className="px-3 py-3">
                            Address
                        </th>

                        <th className="px-3 py-3">
                            Distance
                        </th>

                        <th className="px-3 py-3">
                            Recorded At
                        </th>

                    </tr>

                </thead>


                <tbody>

                    {
                        validPoints.map(
                            (
                                point,
                                index
                            ) => (

                                <tr
                                    key={
                                        point.id ||
                                        index
                                    }
                                    className="
                                        border-b
                                        border-gray-100
                                        last:border-0
                                    "
                                >

                                    <td
                                        className="
                                            px-3
                                            py-3
                                            font-semibold
                                        "
                                    >

                                        {
                                            point.sequence ||
                                            index + 1
                                        }

                                    </td>


                                    <td
                                        className="
                                            px-3
                                            py-3
                                        "
                                    >

                                        <span
                                            className={`
                                                rounded-full
                                                px-2.5
                                                py-1
                                                text-xs
                                                font-medium
                                                ${point.pointType ===
                                                    "start"

                                                    ? "bg-green-100 text-green-700"

                                                    : point.pointType ===
                                                        "end"

                                                        ? "bg-red-100 text-red-700"

                                                        : "bg-indigo-100 text-indigo-700"
                                                }
                                            `}
                                        >

                                            {
                                                point.pointType ||
                                                "tracking"
                                            }

                                        </span>

                                    </td>


                                    <td
                                        className="
                                            px-3
                                            py-3
                                        "
                                    >

                                        {
                                            point.latitude ??
                                            "-"
                                        }

                                    </td>


                                    <td
                                        className="
                                            px-3
                                            py-3
                                        "
                                    >

                                        {
                                            point.longitude ??
                                            "-"
                                        }

                                    </td>


                                    <td
                                        className="
                                            max-w-[250px]
                                            px-3
                                            py-3
                                        "
                                    >

                                        {
                                            point.address ||
                                            "-"
                                        }

                                    </td>


                                    <td
                                        className="
                                            px-3
                                            py-3
                                            font-semibold
                                        "
                                    >

                                        {
                                            formatNumber(
                                                point.distanceFromPreviousKm
                                            )
                                        }

                                        {" km"}

                                    </td>


                                    <td
                                        className="
                                            whitespace-nowrap
                                            px-3
                                            py-3
                                            text-gray-600
                                        "
                                    >

                                        {
                                            formatDateTime(
                                                point.recordedAt
                                            )
                                        }

                                    </td>

                                </tr>

                            )
                        )
                    }

                </tbody>

            </table>

        </div>

    );

};


// ============================================================
// MAIN COMPONENT
// ============================================================

export default function VisitDetail() {

    const {
        id,
    } = useParams();


    // ========================================================
    // STATE
    // ========================================================

    const [
        visit,
        setVisit,
    ] = useState(null);


    const [
        route,
        setRoute,
    ] = useState(null);


    const [
        loading,
        setLoading,
    ] = useState(true);


    const [
        routeLoading,
        setRouteLoading,
    ] = useState(true);


    const [
        routeError,
        setRouteError,
    ] = useState(false);


    // ========================================================
    // LOAD VISIT
    // ========================================================

    useEffect(() => {

        if (!id) {

            setLoading(
                false
            );

            return;

        }


        const load =
            async () => {

                try {

                    setLoading(
                        true
                    );


                    const response =
                        await getVisit(
                            id
                        );


                    console.log(
                        "VISIT DETAIL RESPONSE:",
                        response
                    );


                    const visitData =
                        response?.data?.visit ||
                        response?.data?.data?.visit ||
                        response?.visit ||
                        null;


                    setVisit(
                        visitData
                    );

                } catch (
                error
                ) {

                    console.error(
                        "VISIT DETAIL ERROR:",
                        error
                    );


                    showError(
                        error?.response?.data?.message ||
                        "Unable to load visit."
                    );


                    setVisit(
                        null
                    );

                } finally {

                    setLoading(
                        false
                    );

                }

            };


        load();

    }, [
        id,
    ]);


    // ========================================================
    // LOAD ACTUAL ROUTE
    // ========================================================

    useEffect(() => {

        if (!id) {
            return;
        }


        const loadRoute =
            async () => {

                try {

                    setRouteLoading(
                        true
                    );

                    setRouteError(
                        false
                    );


                    const response =
                        await getVisitRoute(
                            id
                        );


                    console.log(
                        "VISIT ROUTE RESPONSE:",
                        response
                    );


                    const routeData =
                        response?.data?.route ||
                        response?.data?.data?.route ||
                        response?.route ||
                        response?.data ||
                        null;


                    setRoute(
                        routeData
                    );

                } catch (
                error
                ) {

                    console.error(
                        "VISIT ROUTE ERROR:",
                        error
                    );


                    setRoute(
                        null
                    );

                    setRouteError(
                        true
                    );

                } finally {

                    setRouteLoading(
                        false
                    );

                }

            };


        loadRoute();

    }, [
        id,
    ]);


    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {

        return (

            <div
                className="
                    mt-5
                    rounded-xl
                    bg-white
                    p-8
                    text-center
                    text-gray-500
                    shadow-sm
                "
            >

                Loading visit details...

            </div>

        );

    }


    // ========================================================
    // NOT FOUND
    // ========================================================

    if (!visit) {

        return (

            <div
                className="
                    mt-5
                    rounded-xl
                    bg-white
                    p-8
                    text-center
                    shadow-sm
                "
            >

                <p
                    className="
                        mb-4
                        text-gray-600
                    "
                >

                    Visit not found.

                </p>


                <Link
                    to="/admin/visits"
                    className="
                        inline-flex
                        items-center
                        gap-2
                        rounded-lg
                        bg-blue-600
                        px-4
                        py-2
                        text-sm
                        font-medium
                        text-white
                    "
                >

                    <FiArrowLeft />

                    Back to Visits

                </Link>

            </div>

        );

    }


    // ========================================================
    // CUSTOMER
    // ========================================================

    const customer =
        visit.school?.name ||
        visit.partyName ||
        visit.party?.name ||
        "-";


    const customerType =
        visit.school
            ? "School"
            : visit.party || visit.partyName
                ? "Party"
                : visit.visitType ||
                "-";


    // ========================================================
    // ROUTE POINTS
    // ========================================================

    const routePoints =
        getArray(
            route?.points ||
            route?.routePoints ||
            route?.visitRoutePoints ||
            visit?.routePoints
        );


    // ========================================================
    // TOTAL ROUTE KM
    // ========================================================

    const routeTotalKm =
        route?.totalKm ??
        route?.totalDistanceKm ??
        routePoints.reduce(
            (
                total,
                point
            ) =>
                total +
                Number(
                    point.distanceFromPreviousKm ||
                    0
                ),
            0
        );


    // ========================================================
    // EXPENSES
    // ========================================================

    const expenses =
        getArray(
            visit.expenses ||
            visit.expenseDetails ||
            visit.visitExpenses
        );


    const expenseTotal =
        expenses.reduce(
            (
                total,
                expense
            ) => {

                const amount =
                    expense.approvedAmount ??
                    expense.claimedAmount ??
                    expense.amount ??
                    0;


                return (
                    total +
                    Number(
                        amount
                    )
                );

            },
            0
        );


    // ========================================================
    // JOURNEY PLAN
    // ========================================================

    const journeyPlanItem =
        visit.journeyPlanItem ||
        null;


    const plannedSchool =
        journeyPlanItem?.school?.name ||
        null;


    const plannedParty =
        journeyPlanItem?.party?.name ||
        null;


    const plannedLocation =
        plannedSchool ||
        plannedParty ||
        "-";


    const actualLocation =
        customer;


    const hasJourneyPlan =
        Boolean(
            visit.journeyPlanItemId ||
            journeyPlanItem
        );


    const locationMismatch =
        hasJourneyPlan &&
        plannedLocation !== "-" &&
        actualLocation !== "-" &&
        plannedLocation.trim()
            .toLowerCase() !==
        actualLocation.trim()
            .toLowerCase();


    // ========================================================
    // ATTENDANCE
    // ========================================================

    const attendance =
        visit.attendance ||
        null;


    // ========================================================
    // LOCATION
    // ========================================================

    const latitude =
        visit.latitude ??
        visit.checkInLatitude ??
        visit.startLatitude;


    const longitude =
        visit.longitude ??
        visit.checkInLongitude ??
        visit.startLongitude;


    const hasLocation =
        latitude !== null &&
        latitude !== undefined &&
        latitude !== "" &&
        longitude !== null &&
        longitude !== undefined &&
        longitude !== "";


    const mapsUrl =
        hasLocation
            ? `https://www.google.com/maps?q=${latitude},${longitude}`
            : null;


    // ========================================================
    // CHILD COLLECTIONS
    // ========================================================

    const peopleMet =
        getArray(
            visit.peopleMet
        );


    const photos =
        getArray(
            visit.photos
        );


    const followups =
        getArray(
            visit.followups
        );


    // New Visit Details fields are returned directly by the backend.
    // Child collections remain the fallback for older records.
    const personMet =
        visit.personMet ||
        peopleMet[0]?.name ||
        "-";


    const designation =
        visit.designation ||
        peopleMet[0]?.designation ||
        "-";


    const contactNumber =
        visit.contactNumber ||
        peopleMet[0]?.phone ||
        "-";


    const followUp =
        visit.followUp ||
        followups[0]?.nextAction ||
        followups[0]?.action ||
        followups[0]?.remarks ||
        "-";


    const samples =
        getArray(
            visit.samples
        );


    const orders =
        getArray(
            visit.orders
        );


    const payments =
        getArray(
            visit.payments
        );


    // ========================================================
    // TOTAL ORDERS
    // ========================================================

    const orderTotal =
        orders.reduce(
            (
                total,
                order
            ) => {

                return (
                    total +
                    Number(
                        order.estimatedAmount ||
                        order.amount ||
                        order.totalAmount ||
                        0
                    )
                );

            },
            0
        );


    // ========================================================
    // APPROVAL DATA
    // ========================================================

    const approvedBy =
        visit.approvedByEmployee?.name ||
        visit.approvedByUser?.name ||
        visit.approvedBy?.name ||
        "-";


    const approvedAt =
        visit.approvedAt;


    const rejectionReason =
        visit.rejectionReason ||
        visit.rejectedReason ||
        "";


    // ========================================================
    // RETURN
    // ========================================================

    return (

        <div
            className="
                mt-5
                space-y-5
                pb-10
            "
        >

            {/* ==================================================
                HEADER
            ================================================== */}

            <div
                className="
                    rounded-xl
                    bg-white
                    p-5
                    shadow-sm
                "
            >

                <div
                    className="
                        flex
                        flex-col
                        gap-4
                        md:flex-row
                        md:items-center
                        md:justify-between
                    "
                >

                    <div>

                        <div
                            className="
                                mb-2
                                flex
                                items-center
                                gap-2
                                text-sm
                                text-gray-500
                            "
                        >

                            <Link
                                to="/admin/visits"
                                className="
                                    hover:text-blue-600
                                "
                            >
                                Visits
                            </Link>

                            <span>
                                /
                            </span>

                            <span>
                                Visit Details
                            </span>

                        </div>


                        <h1
                            className="
                                text-2xl
                                font-bold
                                text-gray-800
                            "
                        >

                            {
                                customer
                            }

                        </h1>


                        <p
                            className="
                                mt-1
                                text-sm
                                text-gray-500
                            "
                        >

                            {
                                visit.employee?.name ||
                                "-"
                            }

                            {" • "}

                            {
                                formatDate(
                                    visit.visitDate
                                )
                            }

                        </p>

                    </div>


                    <div
                        className="
                            flex
                            flex-wrap
                            items-center
                            gap-2
                        "
                    >

                        <span
                            className={`
                                rounded-full
                                px-4
                                py-2
                                text-xs
                                font-semibold
                                ${getStatusClass(
                                visit.status
                            )}
                            `}
                        >

                            {
                                getStatusLabel(
                                    visit.status
                                )
                            }

                        </span>


                        <Link
                            to="/admin/visits"
                            className="
                                inline-flex
                                items-center
                                gap-2
                                rounded-lg
                                border
                                border-gray-200
                                bg-white
                                px-4
                                py-2
                                text-sm
                                font-medium
                                text-gray-700
                                hover:bg-gray-50
                            "
                        >

                            <FiArrowLeft />

                            Back

                        </Link>

                    </div>

                </div>

            </div>


            {/* ==================================================
                VISIT SUMMARY
            ================================================== */}

            <Section
                title="Visit Summary"
                subtitle="
                    Complete basic information captured for this visit.
                "
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-4
                    "
                >

                    <InfoField
                        label="Salesperson"
                        value={
                            visit.employee?.name ||
                            "-"
                        }
                        icon={FiUser}
                    />


                    <InfoField
                        label="Employee Code"
                        value={
                            visit.employee?.employeeCode ||
                            visit.employee?.code ||
                            "-"
                        }
                    />


                    <InfoField
                        label="Visit Date"
                        value={
                            formatDate(
                                visit.visitDate
                            )
                        }
                        icon={FiCalendar}
                    />


                    <InfoField
                        label="Visit Type"
                        value={
                            customerType
                        }
                    />


                    <InfoField
                        label="Visit Number"
                        value={
                            visit.visitNumber ??
                            "-"
                        }
                    />


                    <InfoField
                        label="Customer / School"
                        value={
                            customer
                        }
                        icon={FiMapPin}
                    />


                    <InfoField
                        label="Person Met"
                        value={
                            personMet
                        }
                        icon={FiUser}
                    />


                    <InfoField
                        label="Designation"
                        value={
                            designation
                        }
                    />


                    <InfoField
                        label="Contact Number"
                        value={
                            contactNumber
                        }
                        icon={FiPhone}
                    />


                    <InfoField
                        label="Purpose"
                        value={
                            visit.purpose
                        }
                    />


                    <InfoField
                        label="Start Time"
                        value={
                            formatDateTime(
                                visit.startAt ||
                                visit.startedAt
                            )
                        }
                        icon={FiClock}
                    />


                    <InfoField
                        label="End Time"
                        value={
                            formatDateTime(
                                visit.endAt ||
                                visit.completedAt ||
                                visit.endedAt
                            )
                        }
                        icon={FiClock}
                    />

                </div>


                <div
                    className="
                        mt-4
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                    "
                >

                    <InfoField
                        label="Assessment"
                        value={
                            visit.assessment
                        }
                    />


                    <InfoField
                        label="Follow-up / Next Action"
                        value={
                            followUp
                        }
                    />


                    <InfoField
                        label="Address"
                        value={
                            visit.address
                        }
                    />


                    <InfoField
                        label="Remarks"
                        value={
                            visit.remarks
                        }
                        className="
                            md:col-span-2
                        "
                    />

                </div>

            </Section>


            {/* ==================================================
                WORKING DATA
            ================================================== */}

            <Section
                title="Working Data Sheet"
                subtitle="
                    Information submitted by the salesperson during the school visit.
                "
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                    "
                >

                    <InfoField
                        label="Purpose"
                        value={
                            visit.purpose
                        }
                        icon={FiFileText}
                    />


                    <InfoField
                        label="Assessment"
                        value={
                            visit.assessment
                        }
                    />


                    <InfoField
                        label="Remarks"
                        value={
                            visit.remarks
                        }
                        className="
                            md:col-span-2
                        "
                    />

                </div>

            </Section>


            {/* ==================================================
                JOURNEY PLAN COMPARISON
            ================================================== */}

            <Section
                title="Journey Plan vs Actual Visit"
                subtitle="
                    Planned location is compared against the actual visit location.
                "
            >

                {
                    !hasJourneyPlan ? (

                        <div
                            className="
                                rounded-lg
                                border
                                border-yellow-200
                                bg-yellow-50
                                p-4
                                text-sm
                                text-yellow-800
                            "
                        >

                            This visit is not linked to a Journey Plan item.

                        </div>

                    ) : (

                        <>

                            {
                                locationMismatch && (

                                    <div
                                        className="
                                            mb-4
                                            rounded-lg
                                            border
                                            border-red-200
                                            bg-red-50
                                            p-4
                                            text-sm
                                            font-medium
                                            text-red-700
                                        "
                                    >

                                        ⚠ Planned and actual locations
                                        do not match. Reviewer should
                                        verify this visit before approval.

                                    </div>

                                )
                            }


                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    gap-4
                                    md:grid-cols-3
                                "
                            >

                                <InfoField
                                    label="Planned Location"
                                    value={
                                        plannedLocation
                                    }
                                    icon={FiMapPin}
                                />


                                <InfoField
                                    label="Actual Visit"
                                    value={
                                        actualLocation
                                    }
                                    icon={FiNavigation}
                                />


                                <InfoField
                                    label="Journey Item"
                                    value={
                                        journeyPlanItem?.itemType ||
                                        "-"
                                    }
                                />


                                <InfoField
                                    label="Sequence"
                                    value={
                                        journeyPlanItem?.sequence ??
                                        "-"
                                    }
                                />


                                <InfoField
                                    label="Planned Time"
                                    value={
                                        journeyPlanItem?.plannedTime ||
                                        "-"
                                    }
                                    icon={FiClock}
                                />


                                <InfoField
                                    label="Purpose"
                                    value={
                                        journeyPlanItem?.purpose ||
                                        "-"
                                    }
                                />

                            </div>

                        </>

                    )
                }

            </Section>


            {/* ==================================================
                CHECK-IN / ATTENDANCE / LOCATION
            ================================================== */}

            <Section
                title="Check-in & Location"
                subtitle="
                    Actual visit check-in and location information.
                "
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-4
                    "
                >

                    <InfoField
                        label="Check-in Time"
                        value={
                            formatDateTime(
                                visit.startAt ||
                                visit.startedAt ||
                                visit.checkInAt
                            )
                        }
                        icon={FiClock}
                    />


                    <InfoField
                        label="Latitude"
                        value={
                            latitude ||
                            "-"
                        }
                    />


                    <InfoField
                        label="Longitude"
                        value={
                            longitude ||
                            "-"
                        }
                    />


                    <InfoField
                        label="Location Address"
                        value={
                            visit.address ||
                            "-"
                        }
                        icon={FiMapPin}
                    />

                </div>


                {
                    attendance && (

                        <div
                            className="
                                mt-4
                                rounded-lg
                                border
                                border-gray-100
                                bg-gray-50
                                p-4
                            "
                        >

                            <h4
                                className="
                                    mb-3
                                    text-sm
                                    font-semibold
                                    text-gray-800
                                "
                            >

                                Attendance

                            </h4>


                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    gap-3
                                    md:grid-cols-3
                                "
                            >

                                <InfoField
                                    label="Attendance Date"
                                    value={
                                        formatDate(
                                            attendance.attendanceDate ||
                                            attendance.date
                                        )
                                    }
                                />


                                <InfoField
                                    label="Check-in"
                                    value={
                                        formatDateTime(
                                            attendance.checkInAt ||
                                            attendance.checkInTime
                                        )
                                    }
                                />


                                <InfoField
                                    label="Status"
                                    value={
                                        attendance.status ||
                                        "-"
                                    }
                                />

                            </div>

                        </div>

                    )
                }


                {
                    mapsUrl && (

                        <div
                            className="
                                mt-4
                            "
                        >

                            <a
                                href={
                                    mapsUrl
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    rounded-lg
                                    bg-blue-600
                                    px-4
                                    py-2
                                    text-sm
                                    font-medium
                                    text-white
                                    hover:bg-blue-700
                                "
                            >

                                <FiExternalLink />

                                Open Check-in Location

                            </a>

                        </div>

                    )
                }

            </Section>


            {/* ==================================================
                ROUTE / KM
            ================================================== */}

            <Section
                title="Actual Travel Route"
                subtitle="
                    Route is based on recorded GPS movement, not the planned Journey Plan route.
                "
                right={

                    <div
                        className="
                            rounded-lg
                            bg-indigo-50
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-indigo-700
                        "
                    >

                        Total:

                        {" "}

                        {
                            formatNumber(
                                routeTotalKm
                            )
                        }

                        {" km"}

                    </div>

                }
            >

                {
                    routeLoading ? (

                        <div
                            className="
                                rounded-lg
                                bg-gray-50
                                p-5
                                text-center
                                text-sm
                                text-gray-500
                            "
                        >

                            Loading actual route...

                        </div>

                    ) : routeError ? (

                        <EmptyState
                            text="
                                Route information could not be loaded.
                            "
                        />

                    ) : (

                        <div
                            className="
                                space-y-5
                            "
                        >

                            <RouteMap
                                points={
                                    routePoints
                                }
                            />


                            <div>

                                <h4
                                    className="
                                        mb-3
                                        text-sm
                                        font-semibold
                                        text-gray-800
                                    "
                                >

                                    Point-to-Point Distance

                                </h4>


                                <RouteTable
                                    points={
                                        routePoints
                                    }
                                />

                            </div>

                        </div>

                    )
                }

            </Section>


            {/* ==================================================
                EXPENSES
            ================================================== */}

            <Section
                title="Detailed Expenses"
                subtitle="
                    Expenses associated with this visit.
                "
                right={

                    <div
                        className="
                            rounded-lg
                            bg-green-50
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-green-700
                        "
                    >

                        Total:

                        {" "}

                        {
                            formatCurrency(
                                expenseTotal
                            )
                        }

                    </div>

                }
            >

                {
                    expenses.length === 0 ? (

                        <EmptyState
                            text="
                                No expense records were returned for this visit.
                            "
                        />

                    ) : (

                        <div
                            className="
                                overflow-x-auto
                            "
                        >

                            <table
                                className="
                                    min-w-full
                                    text-sm
                                "
                            >

                                <thead>

                                    <tr
                                        className="
                                            border-b
                                            border-gray-200
                                            text-left
                                            text-xs
                                            uppercase
                                            text-gray-500
                                        "
                                    >

                                        <th className="px-3 py-3">
                                            Date
                                        </th>

                                        <th className="px-3 py-3">
                                            Type
                                        </th>

                                        <th className="px-3 py-3">
                                            Title
                                        </th>

                                        <th className="px-3 py-3">
                                            From
                                        </th>

                                        <th className="px-3 py-3">
                                            To
                                        </th>

                                        <th className="px-3 py-3">
                                            KM
                                        </th>

                                        <th className="px-3 py-3">
                                            Claimed
                                        </th>

                                        <th className="px-3 py-3">
                                            Approved
                                        </th>

                                        <th className="px-3 py-3">
                                            Status
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {
                                        expenses.map(
                                            (
                                                expense,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        expense.id ||
                                                        index
                                                    }
                                                    className="
                                                        border-b
                                                        border-gray-100
                                                        last:border-0
                                                    "
                                                >

                                                    <td
                                                        className="
                                                            whitespace-nowrap
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        {
                                                            formatDate(
                                                                expense.expenseDate
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                            capitalize
                                                        "
                                                    >

                                                        {
                                                            expense.expenseType ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        {
                                                            expense.title ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        {
                                                            expense.fromLocation ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        {
                                                            expense.toLocation ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        {
                                                            expense.distanceKm
                                                                ? `${formatNumber(
                                                                    expense.distanceKm
                                                                )} km`
                                                                : "-"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            whitespace-nowrap
                                                            px-3
                                                            py-3
                                                            font-semibold
                                                        "
                                                    >

                                                        {
                                                            formatCurrency(
                                                                expense.claimedAmount
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            whitespace-nowrap
                                                            px-3
                                                            py-3
                                                            font-semibold
                                                        "
                                                    >

                                                        {
                                                            formatCurrency(
                                                                expense.approvedAmount
                                                            )
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            px-3
                                                            py-3
                                                        "
                                                    >

                                                        <span
                                                            className="
                                                                rounded-full
                                                                bg-gray-100
                                                                px-2.5
                                                                py-1
                                                                text-xs
                                                                font-medium
                                                                capitalize
                                                            "
                                                        >

                                                            {
                                                                expense.status ||
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>

                                                </tr>

                                            )
                                        )
                                    }

                                </tbody>

                            </table>

                        </div>

                    )
                }

            </Section>


            {/* ==================================================
                APPROVAL / REVIEW
            ================================================== */}

            <Section
                title="Approval & Review"
                subtitle="
                    Review information recorded against this visit.
                "
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-4
                    "
                >

                    <InfoField
                        label="Status"
                        value={
                            getStatusLabel(
                                visit.status
                            )
                        }
                    />


                    <InfoField
                        label="Approved By"
                        value={
                            approvedBy
                        }
                        icon={FiUser}
                    />


                    <InfoField
                        label="Approved At"
                        value={
                            formatDateTime(
                                approvedAt
                            )
                        }
                        icon={FiClock}
                    />


                    <InfoField
                        label="Correction / Rejection Reason"
                        value={
                            rejectionReason ||
                            "-"
                        }
                        icon={FiFileText}
                    />

                </div>


                {
                    rejectionReason && (

                        <div
                            className="
                                mt-4
                                rounded-lg
                                border
                                border-red-200
                                bg-red-50
                                p-4
                                text-sm
                                text-red-700
                            "
                        >

                            <div
                                className="
                                    mb-1
                                    flex
                                    items-center
                                    gap-2
                                    font-semibold
                                "
                            >

                                <FiXCircle />

                                Review Reason

                            </div>


                            {
                                rejectionReason
                            }

                        </div>

                    )
                }


                {
                    visit.status ===
                    "approved" && (

                        <div
                            className="
                                mt-4
                                flex
                                items-center
                                gap-2
                                rounded-lg
                                border
                                border-green-200
                                bg-green-50
                                p-4
                                text-sm
                                font-medium
                                text-green-700
                            "
                        >

                            <FiCheckCircle />

                            This visit has been approved.

                        </div>

                    )
                }


                {
                    visit.status ===
                    "correction_requested" && (

                        <div
                            className="
                                mt-4
                                flex
                                items-center
                                gap-2
                                rounded-lg
                                border
                                border-orange-200
                                bg-orange-50
                                p-4
                                text-sm
                                font-medium
                                text-orange-700
                            "
                        >

                            <FiEdit2 />

                            Correction has been requested for this visit.

                        </div>

                    )
                }

            </Section>


            {/* ==================================================
                RAW LOCATION SUMMARY
            ================================================== */}

            <div
                className="
                    rounded-xl
                    border
                    border-gray-100
                    bg-white
                    p-5
                    shadow-sm
                "
            >

                <div
                    className="
                        flex
                        flex-col
                        gap-3
                        md:flex-row
                        md:items-center
                        md:justify-between
                    "
                >

                    <div>

                        <h3
                            className="
                                font-bold
                                text-gray-800
                            "
                        >

                            Visit Review Summary

                        </h3>


                        <p
                            className="
                                mt-1
                                text-xs
                                text-gray-500
                            "
                        >

                            {
                                routePoints.length
                            }

                            {" actual GPS points • "}

                            {
                                formatNumber(
                                    routeTotalKm
                                )
                            }

                            {" km travelled • "}

                            {
                                expenses.length
                            }

                            {" expense records"}

                        </p>

                    </div>


                    {
                        hasLocation && (

                            <a
                                href={
                                    mapsUrl
                                }
                                target="_blank"
                                rel="noreferrer"
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    rounded-lg
                                    bg-gray-800
                                    px-4
                                    py-2
                                    text-sm
                                    font-medium
                                    text-white
                                    hover:bg-gray-900
                                "
                            >

                                <FiMapPin />

                                Open Location

                            </a>

                        )
                    }

                </div>

            </div>

        </div>

    );

}